/**
 * Debug Logging Utility
 *
 * Centralized logging system that can be enabled/disabled for production
 * Replaces direct console.log usage throughout the application
 */

import { devLog } from '../utils/devLog';
import logger from './logger';

// Check if we're in development mode
const isDevelopment = process.env.NODE_ENV === 'development';

// Enable debug logging (can be overridden via localStorage)
const isDebugEnabled = () => {
  if (typeof window !== 'undefined') {
    const debugOverride = localStorage.getItem('quicklog_debug');
    if (debugOverride !== null) {
      return debugOverride === 'true';
    }
  }
  return isDevelopment;
};

/**
 * Debug logging function - only outputs in development or when explicitly enabled
 */
export const debug = {
  log: (...args) => {
    if (isDebugEnabled()) {
      logger.log('[FleetSkipper Debug]', ...args);
    }
  },
  
  warn: (...args) => {
    if (isDebugEnabled()) {
      logger.warn('[FleetSkipper Warning]', ...args);
    }
  },
  
  error: (...args) => {
    // Always log errors, but prefix them for identification
    logger.error('[FleetSkipper Error]', ...args);
  },
  
  info: (...args) => {
    if (isDebugEnabled()) {
      logger.info('[FleetSkipper Info]', ...args);
    }
  }
};

/**
 * Production-safe logging for important events
 * These will always be logged but in a structured format
 */
export const audit = {
  log: (action, data) => {
    const logEntry = {
      timestamp: new Date().toISOString(),
      action,
      data,
      environment: process.env.NODE_ENV
    };
    
    if (isDebugEnabled()) {
      logger.log('[FleetSkipper Audit]', logEntry);
    }
    
    // In production, these could be sent to an external logging service
    // For now, we'll store them in a way that won't clutter the console
  },
  
  error: (action, error, context = {}) => {
    const errorEntry = {
      timestamp: new Date().toISOString(),
      action,
      error: error.message || error,
      stack: error.stack,
      context,
      environment: process.env.NODE_ENV
    };
    
    logger.error('[FleetSkipper Audit Error]', errorEntry);
  }
};

/**
 * Enable/disable debug logging at runtime
 * Usage: enableDebug(true) or enableDebug(false)
 */
export const enableDebug = (enabled) => {
  if (typeof window !== 'undefined') {
    localStorage.setItem('quicklog_debug', enabled.toString());
    debug.log(`Debug logging ${enabled ? 'enabled' : 'disabled'}`);
  }
};

/**
 * Legacy console replacement for gradual migration
 * @deprecated Use debug.log instead
 */
export const console_log = (...args) => {
  debug.log('[LEGACY]', ...args);
};

export default debug;